// Admin-only Settings > Third Party API.
//
// Two surfaces:
//
//   * /admin/integrations  — list / detail / put / test / delete
//                            (admin-only; requires Permission.MANAGE_ORGANISATION)
//   * /integrations/runtime/{key} — resolved active config for any
//                                    authenticated user; powers the
//                                    address autocomplete.
//
// The runtime config is cached with a short ``staleTime`` so a fresh
// session doesn't hammer the backend on every page transition; admin
// mutations invalidate it so the change reflects within the cache
// window.

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { apiClient } from "./client";

// ---------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------

export interface IntegrationFieldSchema {
  name: string;
  type: "string" | "boolean";
  label: string;
  secret: boolean;
  required: boolean;
  placeholder: string;
  help_text: string;
}

export interface IntegrationSchema {
  key: string;
  display_name: string;
  description: string;
  has_test: boolean;
  fields: IntegrationFieldSchema[];
}

export type ConfigSource = "database" | "environment" | "not_configured";

export interface IntegrationSummary {
  key: string;
  display_name: string;
  description: string;
  enabled: boolean;
  configured: boolean;
  source: ConfigSource;
  has_test: boolean;
  last_tested_at: string | null;
  last_test_succeeded: boolean | null;
  last_test_error: string | null;
}

export interface IntegrationDetail {
  key: string;
  display_name: string;
  description: string;
  enabled: boolean;
  schema: IntegrationSchema;
  config_masked: Record<string, unknown>;
  config_raw: Record<string, unknown>;
  source: ConfigSource;
  has_test: boolean;
  last_tested_at: string | null;
  last_test_succeeded: boolean | null;
  last_test_error: string | null;
  updated_at: string | null;
  updated_by_user_id: string | null;
}

export interface IntegrationPutBody {
  config: Record<string, unknown>;
  enabled: boolean;
}

export interface IntegrationTestResult {
  success: boolean;
  message: string;
  details: string | null;
}

export interface RuntimeConfig {
  key: string;
  source: ConfigSource;
  config: Record<string, unknown>;
}

// ---------------------------------------------------------------------
// REST surface
// ---------------------------------------------------------------------

const ADMIN_BASE = "/admin/integrations";
const RUNTIME_BASE = "/integrations/runtime";

export const TP_LIST_KEY = ["third-party", "list"] as const;
export const TP_DETAIL_KEY = (key: string) =>
  ["third-party", "detail", key] as const;
export const TP_RUNTIME_KEY = (key: string) =>
  ["third-party", "runtime", key] as const;

export async function listIntegrations(): Promise<IntegrationSummary[]> {
  const { data } = await apiClient.get<IntegrationSummary[]>(ADMIN_BASE);
  return data;
}

export async function getIntegrationDetail(
  key: string,
): Promise<IntegrationDetail> {
  const { data } = await apiClient.get<IntegrationDetail>(
    `${ADMIN_BASE}/${encodeURIComponent(key)}`,
  );
  return data;
}

export async function putIntegration(
  key: string,
  body: IntegrationPutBody,
): Promise<IntegrationSummary> {
  const { data } = await apiClient.put<IntegrationSummary>(
    `${ADMIN_BASE}/${encodeURIComponent(key)}`,
    body,
  );
  return data;
}

export async function testIntegration(
  key: string,
  config: Record<string, unknown> | null,
): Promise<IntegrationTestResult> {
  const body = config === null ? {} : { config };
  const { data } = await apiClient.post<IntegrationTestResult>(
    `${ADMIN_BASE}/${encodeURIComponent(key)}/test`,
    body,
  );
  return data;
}

export async function deleteIntegration(key: string): Promise<void> {
  await apiClient.delete(`${ADMIN_BASE}/${encodeURIComponent(key)}`);
}

export async function getRuntimeConfig(key: string): Promise<RuntimeConfig> {
  const { data } = await apiClient.get<RuntimeConfig>(
    `${RUNTIME_BASE}/${encodeURIComponent(key)}`,
  );
  return data;
}

// ---------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------

export function useIntegrationsList() {
  return useQuery({
    queryKey: TP_LIST_KEY,
    queryFn: listIntegrations,
    staleTime: 30_000,
  });
}

export function useIntegrationDetail(key: string | null) {
  return useQuery({
    queryKey: TP_DETAIL_KEY(key ?? ""),
    queryFn: () => getIntegrationDetail(key as string),
    enabled: key !== null,
    staleTime: 30_000,
  });
}

export function usePutIntegration(key: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: IntegrationPutBody) => putIntegration(key, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TP_LIST_KEY });
      qc.invalidateQueries({ queryKey: TP_DETAIL_KEY(key) });
      qc.invalidateQueries({ queryKey: TP_RUNTIME_KEY(key) });
    },
  });
}

export function useTestIntegration(key: string) {
  return useMutation({
    mutationFn: (config: Record<string, unknown> | null) =>
      testIntegration(key, config),
  });
}

export function useDeleteIntegration(key: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => deleteIntegration(key),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TP_LIST_KEY });
      qc.invalidateQueries({ queryKey: TP_DETAIL_KEY(key) });
      qc.invalidateQueries({ queryKey: TP_RUNTIME_KEY(key) });
    },
  });
}

export function useRuntimeConfig(key: string) {
  // Runtime config powers features for any authenticated user. Cache
  // for 60 s to match the backend resolver's TTL — admins changing
  // the config see the new value within one window.
  return useQuery({
    queryKey: TP_RUNTIME_KEY(key),
    queryFn: () => getRuntimeConfig(key),
    staleTime: 60_000,
    retry: false,
  });
}
